import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Factory, CheckCircle2, AlertTriangle, Ban, Euro, Sparkles, Gauge, CalendarClock, Truck, X, ChevronRight, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { daysColor } from "@/pages/Production";
import { VBars } from "@/components/MiniBars";
import ProductionKpis from "@/components/ProductionKpis";

function Kpi({ icon: Icon, label, value, sub, accent, testid, onClick }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp data-testid={testid} onClick={onClick}
      className={`panel rounded-[4px] p-5 flex flex-col justify-between min-h-[124px] text-left w-full ${onClick ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all" : ""}`}
      style={{ borderTop: `3px solid ${accent}` }}>
      <div className="flex items-center justify-between">
        <span className="lbl">{label}</span>
        <Icon size={18} style={{ color: accent }} strokeWidth={1.9} />
      </div>
      <div>
        <div className="font-display font-700 text-4xl t-head tracking-tight leading-none">{value}</div>
        {sub && <div className="text-xs t-muted mt-1">{sub}</div>}
      </div>
    </Comp>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const canSeeValue = ["management", "sales", "admin", "amministrazione"].includes(user.role);
  const [k, setK] = useState(null);
  const [insight, setInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {bucket, label, accent}
  const [drillItems, setDrillItems] = useState(null);

  useEffect(() => { api.get("/dashboard/kpis").then((r) => setK(r.data)).catch(() => toast.error("Error")); }, []);

  const euro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

  const BUCKETS = {
    in_produzione: { label: t("orders_active"), accent: "#33559d" },
    completata: { label: t("orders_completed"), accent: "#2e7d32" },
    a_rischio: { label: t("orders_at_risk"), accent: "#b45309" },
    overdue: { label: t("orders_overdue"), accent: "#9c1e2b" },
    pronta: { label: t("orders_ready"), accent: "#0f766e" },
  };

  useEffect(() => {
    const b = sp.get("drill");
    if (b && BUCKETS[b]) {
      setDrill({ bucket: b, ...BUCKETS[b] });
      setDrillItems(null);
      api.get(`/dashboard/orders?bucket=${b}`).then((r) => setDrillItems(r.data)).catch(() => { toast.error("Error"); setDrillItems([]); });
    } else {
      setDrill(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const openDrill = (bucket) => setSp({ drill: bucket });
  const closeDrill = () => setSp({});

  const genInsight = async () => {
    setAiLoading(true);
    try { const { data } = await api.post("/ai/insights", { lang }); setInsight(data.insight); }
    catch { toast.error("AI non disponibile"); } finally { setAiLoading(false); }
  };

  if (!k) return <div className="p-8 t-muted">…</div>;
  const chartData = k.phase_load.map((p) => ({
    label: t(p.phase), value: p.in_progress + p.pending,
    color: p.blocked > 0 ? "#9c1e2b" : "#33559d",
  }));

  return (
    <div className="p-5 lg:p-10 max-w-[1600px] mx-auto animate-rise">
      <div className="mb-6">
        <h1 className="font-display font-700 text-3xl lg:text-4xl t-head tracking-tight">{t("dashboard")}</h1>
        <p className="t-muted mt-1 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#2e7d32] animate-pulse inline-block" />{t("real_time")}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
        <Kpi testid="kpi-active" icon={Factory} label={t("orders_active")} value={k.orders_active} accent="#33559d" onClick={() => openDrill("in_produzione")} />
        <Kpi testid="kpi-completed" icon={CheckCircle2} label={t("orders_completed")} value={k.orders_completed} accent="#2e7d32" onClick={() => openDrill("completata")} />
        <Kpi testid="kpi-at-risk" icon={AlertTriangle} label={t("orders_at_risk")} value={k.orders_at_risk} accent="#b45309" onClick={() => openDrill("a_rischio")} />
        <Kpi testid="kpi-overdue" icon={Ban} label={t("orders_overdue")} value={k.orders_overdue} accent="#9c1e2b" onClick={() => openDrill("overdue")} />
        <Kpi testid="kpi-ready" icon={PackageCheck} label={t("orders_ready")} value={k.orders_ready ?? 0} accent="#0f766e" onClick={() => openDrill("pronta")} />
      </div>

      <div className={`grid ${canSeeValue ? "grid-cols-2" : "grid-cols-1"} gap-3 lg:gap-4 mt-4`}>
        {canSeeValue && <Kpi testid="kpi-revenue" icon={Euro} label={t("revenue")} value={euro(k.revenue)} sub={`${k.orders_total} ${t("work_orders")}`} accent="#33559d" />}
        <Kpi testid="kpi-efficiency" icon={Gauge} label={t("avg_efficiency")} value={`${k.avg_efficiency}%`} sub={t("productivity_sub")} accent="#2e7d32" />
      </div>

      <div className="mt-8">
        <ProductionKpis compact />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="panel rounded-[4px] p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-600 text-lg t-head tracking-tight">{t("phase_load")}</h3>
            <span className="font-mono text-[10px] t-faint uppercase tracking-wider">work in progress</span>
          </div>
          <VBars data={chartData} height={260} />
        </div>

        <div className="panel rounded-[4px] p-5 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#33559d]" />
            <h3 className="font-display font-600 text-lg t-head tracking-tight">{t("ai_insights")}</h3>
          </div>
          <div className="flex-1 text-sm t-body whitespace-pre-line leading-relaxed" data-testid="ai-insight-text">
            {insight || <span className="t-faint">—</span>}
          </div>
          <button data-testid="generate-insight-button" onClick={genInsight} disabled={aiLoading}
            className="btn-primary btn-industrial mt-4 flex items-center justify-center gap-2 py-2.5 text-sm rounded-[3px] disabled:opacity-60">
            <Sparkles size={15} /> {aiLoading ? t("generating") : t("generate_ai")}
          </button>
        </div>
      </div>

      <div className="panel rounded-[4px] p-5 mt-4">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock size={18} className="text-[#33559d]" />
          <h3 className="font-display font-600 text-lg t-head tracking-tight">{t("upcoming")}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {k.upcoming.map((u, idx) => (
            <div key={`${u.code}-${idx}`} data-testid={`upcoming-${u.code}`} className="border hairline rounded-[3px] p-3 bg-white hover:border-[#33559d] transition-colors" style={{ borderLeft: `3px solid ${daysColor(u.days_to_due)}` }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#33559d] text-xs">{u.code}</span>
                <span className="font-mono text-xs font-600" style={{ color: daysColor(u.days_to_due) }}>{u.days_to_due}gg{u.at_risk ? " ⚠" : ""}</span>
              </div>
              <div className="t-head text-sm mt-1 truncate">{u.product}</div>
              <div className="text-[11px] t-muted">{u.client}</div>
              <div className="w-full h-1 bg-[#eef1f5] mt-2 rounded-full"><div className="h-full bg-[#33559d] rounded-full" style={{ width: `${u.progress}%` }} /></div>
            </div>
          ))}
          {k.upcoming.length === 0 && <div className="t-faint text-sm">—</div>}
        </div>
      </div>

      <div className="panel rounded-[4px] p-5 mt-4" data-testid="planned-deliveries-panel" style={{ borderTop: "3px solid #12a150" }}>
        <div className="flex items-center gap-2 mb-1">
          <Truck size={18} className="text-[#0f7a3d]" />
          <h3 className="font-display font-600 text-lg t-head tracking-tight">{t("planned_deliveries") || "Consegne in partenza"}</h3>
          {(k.planned_deliveries_count || 0) > 0 && <span data-testid="planned-deliveries-count" className="font-mono text-xs bg-[#eafaf0] text-[#0f7a3d] px-2 py-0.5 rounded-full font-600">{k.planned_deliveries_count}</span>}
        </div>
        <p className="t-faint text-xs mb-4">{t("planned_deliveries_hint") || "Macchine con consegna pianificata: verranno conteggiate solo dopo la partenza effettiva."}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(k.planned_deliveries || []).map((u, idx) => (
            <div key={`${u.code}-${idx}`} data-testid={`planned-delivery-${u.code}`} className="border hairline rounded-[3px] p-3 bg-white hover:border-[#12a150] transition-colors" style={{ borderLeft: "3px solid #12a150" }}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#0f7a3d] text-xs">{u.code}</span>
                <span className="font-mono text-xs t-muted">{u.due_date ? new Date(u.due_date).toLocaleDateString("it-IT") : "—"}{u.at_risk ? " ⚠" : ""}</span>
              </div>
              <div className="t-head text-sm mt-1 truncate">{u.product}</div>
              <div className="text-[11px] t-muted">{u.client}</div>
              <div className="w-full h-1 bg-[#eef1f5] mt-2 rounded-full"><div className="h-full bg-[#12a150] rounded-full" style={{ width: `${u.progress}%` }} /></div>
            </div>
          ))}
          {(k.planned_deliveries || []).length === 0 && <div className="t-faint text-sm">{t("no_planned_deliveries") || "Nessuna consegna pianificata in attesa di partenza"}</div>}
        </div>
      </div>

      {drill && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={closeDrill}>
          <div className="panel h-full w-full max-w-md p-5 overflow-y-auto animate-rise" onClick={(e) => e.stopPropagation()} data-testid="drill-panel" style={{ borderTop: `3px solid ${drill.accent}` }}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-700 text-lg t-head tracking-tight">{drill.label}</h3>
              <button data-testid="drill-close" onClick={closeDrill} className="t-muted hover:t-head"><X size={20} /></button>
            </div>
            <p className="t-faint text-xs mb-4">{drillItems == null ? "Caricamento…" : `${drillItems.length} commesse`}</p>
            <div className="space-y-2">
              {drillItems == null && <div className="t-faint text-sm py-8 text-center">…</div>}
              {drillItems != null && drillItems.length === 0 && <div className="t-faint text-sm py-8 text-center">Nessuna commessa in questa categoria.</div>}
              {(drillItems || []).map((o) => (
                <button key={o.code} data-testid={`drill-order-${o.code}`} onClick={() => navigate(`/production/order/${encodeURIComponent(o.code)}`)}
                  className="w-full text-left border hairline rounded-[3px] p-3 bg-white hover:border-[#33559d] hover:bg-[#f7f9fc] transition-colors flex items-center gap-3"
                  style={{ borderLeft: `3px solid ${o.days_to_due != null ? daysColor(o.days_to_due) : drill.accent}` }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[#33559d] text-xs">{o.code}</span>
                      <span className="font-mono text-xs font-600" style={{ color: o.days_to_due != null ? daysColor(o.days_to_due) : "#8a97a8" }}>
                        {o.days_to_due != null ? `${o.days_to_due}gg` : "—"}{o.at_risk ? " ⚠" : ""}
                      </span>
                    </div>
                    <div className="t-head text-sm mt-0.5 truncate">{o.product}</div>
                    <div className="text-[11px] t-muted truncate">{o.client}{o.due_date ? ` · ${new Date(o.due_date).toLocaleDateString("it-IT")}` : ""}{o.value != null ? ` · ${euro(o.value)}` : ""}</div>
                    <div className="w-full h-1 bg-[#eef1f5] mt-2 rounded-full"><div className="h-full rounded-full" style={{ width: `${o.progress}%`, background: drill.accent }} /></div>
                  </div>
                  <ChevronRight size={16} className="t-faint shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
